import axios from 'axios'
import {API_BASE_URL, BACKEND_ENDPOINT, HTTP_UNAUTHORIZED} from '../Constants'
import {renewToken} from '../components/authentication/AuthEndPoints'
import {
    clearStorage,
    getAccessToken,
    getClientId,
    getRefreshToken,
    getSessionId,
    getUsername,
    getXApiKey,
    setAccessToken,
} from '../components/authentication/AuthUtils'
import {showErrorModal} from '../components/error/ErrorService'
import {severityNotification} from '../components/notification/NotificationConstant'
import {showNotification} from '../components/notification/NotificationService'
import {openPage} from '../utils/NavigatorUtils'

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Origin-Device': 'web',
    },
})

axiosInstance.interceptors.request.use(
    function (config) {
        config.headers['X-Api-Key'] = !!getXApiKey()
            ? getXApiKey()
            : 'BX001-STANDARD'

        // Do something before request is sent
        if (!!getClientId()) {
            if (!!config.params) {
                config.params['clientId'] = getClientId()
            } else {
                config.params = {
                    clientId: getClientId(),
                }
            }
        }

        if (!!getSessionId()) {
            config.headers['SessionId'] = getSessionId()
        }

        if (!!getUsername()) {
            config.headers['Username'] = getUsername()
        }

        return config
    },
    function (error) {
        // Do something with request error
        return Promise.reject(error)
    },
)

// Add a response interceptor
axiosInstance.interceptors.response.use(
    function (response) {
        // Any status code that lie within the range of 2xx cause this function to trigger
        // Do something with\ response data
        return response
    },
    async function (error) {
        // const history = useHistory()

        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Do something with response error
        let res = error.response
        let originalRequest = error.config

        if (res) {
            switch (res.status) {
                case 302:
                    if (res.data && res.data.redirectUrl) {
                        window.location.href = res.data.redirectUrl
                    }
                    break
                case 400:
                    if (res.data && typeof res.data === 'string') {
                        showNotification(severityNotification.warn, res.data)
                    } else {
                        showNotification(
                            severityNotification.warn,
                            'Your request is invalid !',
                        )
                    }
                    break
                case 404:
                    openPage('/404')
                    break
                case 401:
                    if (res.config) {
                        if (
                            res.config.url ===
                            `${BACKEND_ENDPOINT}/authentication/getToken`
                        ) {
                            showNotification(
                                severityNotification.info,
                                'Invalid Credentials',
                            )

                            await Promise.resolve(
                                res.data === HTTP_UNAUTHORIZED,
                            )

                            return
                        } else {
                            if (!originalRequest._retry) {
                                originalRequest._retry = true

                                delete axiosInstance.defaults.headers.common[
                                    'Authorization'
                                ]

                                if (!!getRefreshToken()) {
                                    await renewToken({
                                        refreshToken: getRefreshToken(),
                                    }).then(async data => {
                                        if (data?.token) {
                                            setAccessToken(data.token)

                                            axiosInstance.defaults.headers.common[
                                                'Authorization'
                                            ] = `Bearer ${getAccessToken()}`

                                            originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`

                                            error = await Promise.resolve(
                                                await axiosInstance.request(
                                                    originalRequest,
                                                ),
                                            )
                                        } else {
                                            clearStorage()
                                            openPage('/')
                                        }
                                    })
                                } else {
                                    clearStorage()
                                    openPage('/')
                                }
                            }
                        }
                    }
                    break

                case 403:
                    clearStorage()

                    break
                case 409:
                    showNotification(
                        severityNotification.info,
                        'You account has been temporarily blocked !!',
                    )
                    break
                case 429:
                    showErrorModal()
                    break
                case 440:
                    clearStorage()
                    openPage('/')
                    break
                case 500:
                    showNotification(
                        severityNotification.error,
                        'Problem occurs. Please try again !',
                    )
                    break
                case 503:
                    showNotification(
                        severityNotification.info,
                        'Our service is unavailable at the moment !',
                    )
                    break
                default:
            }
        }

        if (originalRequest?._retry) {
            return Promise.resolve(error)
        }
        return Promise.reject(error)
    },
)

export default axiosInstance
